import dayjs from 'dayjs'
import type { BoosterMilestoneFormValues, BoosterProgramFormValues } from 'Modules'

export const REQUIRED_PROGRAM_FIELDS: (keyof BoosterProgramFormValues)[] = [
  'title',
  'period',
  'description',
  'terms',
  'enrollmentPeriodValue',
  'targetUsers',
  'participantLimitType',
]

export const REQUIRED_MILESTONE_FIELDS = (
  m: Partial<BoosterMilestoneFormValues> | undefined
): boolean => {
  if (!m) return false
  if (m.threshold == null || m.rewardType == null) return false
  if (m.totalSupply == null || m.codeExpiry == null) return false
  if (!m.linkedPtos || m.linkedPtos.length === 0) return false

  switch (m.rewardType) {
    case 'DISCOUNT_CODE':
      return m.discountValue != null
    case 'APR_BOOSTER_CODE':
      return m.boostAprValue != null
    case 'CASHBACK_TOKEN_CODE':
      return m.cashbackRate != null && !!m.tokenType
    case 'PTO_BONUS_CODE':
      return m.bonusBuy != null && m.bonusGet != null
    default:
      return false
  }
}

const isFilled = (v: unknown): boolean =>
  v != null && v !== '' && !(Array.isArray(v) && v.length === 0)

export const hasAnyRequired = (values: Partial<BoosterProgramFormValues>): boolean => {
  if (REQUIRED_PROGRAM_FIELDS.some((f) => isFilled(values[f]))) return true
  const milestones = values.milestones ?? []
  return milestones.some((m) => REQUIRED_MILESTONE_FIELDS(m))
}

export const hasAllRequired = (values: Partial<BoosterProgramFormValues>): boolean => {
  if (REQUIRED_PROGRAM_FIELDS.some((f) => !isFilled(values[f]))) return false
  if (values.participantLimitType === 'LIMITED' && values.participantLimit == null) return false
  const milestones = values.milestones ?? []
  if (milestones.length === 0) return false
  return milestones.every((m) => REQUIRED_MILESTONE_FIELDS(m))
}

export type BoosterProgramPayloadStatus = 'DRAFT' | 'ACTIVE'

export const buildPayload = (
  values: BoosterProgramFormValues,
  status: BoosterProgramPayloadStatus
) => {
  const [start, end] = values.period ?? []
  return {
    ...values,
    status,
    startDate: start ? dayjs(start).toISOString() : null,
    endDate: end ? dayjs(end).toISOString() : null,
  }
}
